import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIsMutating, useMutation, useMutationState, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { isPlannerV2Action } from '../../hooks/staged-actions/confidence'
import { getPendingQuestions } from '../../hooks/staged-actions/prerequisites'
import type { StagedAction } from '../../hooks/staged-actions/types'
import { STAGED_ACTIONS_QUERY_KEY, stagedActionExpandedQueryKey } from '../../hooks/staged-actions/utils'
import { isUndrafted } from '../meeting-view/action-items/send-readiness'
import { agentDraftingUntil } from './agent-drafting'

/** Keyed per action, so the body and the footer read the same draft request. */
const draftMutationKey = (actionId: string | undefined) => ['planner-v2-draft', actionId]

export interface ActionDraftControl {
  /** The body is blank and no draft is running, so the card offers to draft it. */
  canDraft: boolean
  /** The body is being written, so the card shows a placeholder instead of the empty editor. */
  isDrafting: boolean
  /** The last draft asked for failed; the body stays blank and editable. */
  failed: boolean
  draft: () => void
  /** What the draft button says: an event is drafted, a Slack or Gmail action is a message. */
  label: string
}

/** Drafts a planner v2 message staged blank; after a 202 the agent's drafting mark, not the request, says it's still being written. */
export function useDraftAction(action: StagedAction | undefined): ActionDraftControl {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const actionId = action?.id
  // An action still asking drafts from the answers; until then its questions stand in for the Draft button.
  const needsBody =
    !!action &&
    isPlannerV2Action(action) &&
    !action.lastInvokedAt &&
    isUndrafted(action) &&
    !getPendingQuestions(action)

  const { mutate } = useMutation({
    mutationKey: draftMutationKey(actionId),
    mutationFn: async (stagedActionId: string) => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute('api/v1/planner-v2/draft'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stagedActionId }),
      })
      if (!response.ok) throw new Error(`Failed to draft the action (${response.status})`)
    },
    // The view reads its own expanded copy of the action, which the list refetch doesn't touch
    onSettled: (_data, _error, stagedActionId) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: stagedActionExpandedQueryKey(stagedActionId) }),
        queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] }),
      ]),
    onError: (error) => console.error(error),
  })

  const isPending = useIsMutating({ mutationKey: draftMutationKey(actionId) }) > 0
  const lastStatus = useMutationState({
    filters: { mutationKey: draftMutationKey(actionId) },
    select: (mutation) => mutation.state.status,
  }).at(-1)
  const isError = lastStatus === 'error'

  const agentUntil = action ? agentDraftingUntil(action) : null
  const [, rerender] = useState(0)
  // The mark only expires with time, which no refetch reports
  useEffect(() => {
    if (agentUntil === null) return
    const timer = setTimeout(() => rerender((n) => n + 1), agentUntil - Date.now())
    return () => clearTimeout(timer)
  }, [agentUntil])

  const isDrafting = isPending || agentUntil !== null
  const draft = useCallback(() => {
    if (actionId) mutate(actionId)
  }, [actionId, mutate])

  const label = isDrafting
    ? 'Drafting…'
    : action?.toolSet === 'google_calendar'
      ? 'Draft the event'
      : 'Draft the message'
  return useMemo(
    () => ({
      canDraft: needsBody && !isDrafting,
      isDrafting,
      failed: isError && needsBody && !isDrafting,
      draft,
      label,
    }),
    [needsBody, isDrafting, isError, draft, label],
  )
}
